import React from "react";
import { DashboardPage } from "@/pages";
import { DashboardLayout } from "@/components/layouts";
import { ALL_ROLES, MANAGER_ROLES, USER_ROLES } from "./userRoles";


// Helper function for lazy loading named exports
const lazyNamed = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ [key: string]: T }>,
  componentName: string
) =>
  React.lazy(() =>
    importFn().then((module) => ({ default: module[componentName] as T }))
  );

// Lazy-loaded public pages
const NotFoundPage = lazyNamed(
  () => import("@/pages/public/NotFoundPage/NotFoundPage"),
  "NotFoundPage"
);
const ServerErrorPage = lazyNamed(
  () => import("@/pages/public/ServerErrorPage/ServerErrorPage"),
  "ServerErrorPage"
);
const SigninPage = lazyNamed(
  () => import("@/pages/public/SigninPage/SigninPage"),
  "SigninPage"
);
const SignupPage = lazyNamed(
  () => import("@/pages/public/SignupPage/SignupPage"),
  "SignupPage"
);
const ForgotPasswordPage = lazyNamed(
  () => import("@/pages/public/ForgotPasswordPage/ForgotPasswordPage"),
  "ForgotPasswordPage"
);
const ResetPasswordPage = lazyNamed(
  () => import("@/pages/public/ResetPasswordPage/ResetPasswordPage"),
  "ResetPasswordPage"
);
const AcceptInvitationPage = lazyNamed(
  () => import("@/pages/public/AcceptInvitationPage/AcceptInvitationPage"),
  "AcceptInvitationPage"
);

// Lazy-loaded private pages
const CarsPage = lazyNamed(
  () => import("@/pages/private/CarsPage/CarsPage"),
  "CarsPage"
);
// const RefuelsPage = lazyNamed(
//   () => import("@/pages/private/RefuelsPage/RefuelsPage"),
//   "RefuelsPage"
// );
const TagsPage = lazyNamed(
  () => import("@/pages/private/TagsPage/TagsPage"),
  "TagsPage"
);
// const CheckpointsPage = lazyNamed(
//   () => import("@/pages/private/CheckpointsPage/CheckpointsPage"),
//   "CheckpointsPage"
// );
// const ExpensesPage = lazyNamed(
//   () => import("@/pages/private/ExpensesPage/ExpensesPage"),
//   "ExpensesPage"
// );
const AccountSettingsPage = lazyNamed(
  () => import("@/pages/private/AccountSettingsPage/AccountSettingsPage"),
  "AccountSettingsPage"
);
const SubscriptionPage = lazyNamed(
  () => import("@/pages/private/SubscriptionPage/SubscriptionPage"),
  "SubscriptionPage"
);
const UserInvitationsPage = lazyNamed(
  () => import("@/pages/private/UserInvitationsPage/UserInvitationsPage"),
  "UserInvitationsPage"
);
const UsersPage = lazyNamed(
  () => import("@/pages/private/UsersPage/UsersPage"),
  "UsersPage"
);
const UserPreferencesPage = lazyNamed(
  () => import("@/pages/private/UserPreferencesPage/UserPreferencesPage"),
  "UserPreferencesPage"
);
const ServiceIntervalsPage = lazyNamed(
  () => import("@/pages/private/ServiceIntervalsPage/ServiceIntervalsPage"),
  "ServiceIntervalsPage"
);
const UpcomingServicesPage = lazyNamed(
  () => import("@/pages/private/UpcomingServicesPage/UpcomingServicesPage"),
  "UpcomingServicesPage"
);
const UserProfilePage = lazyNamed(
  () => import("@/pages/private/UserProfilePage/UserProfilePage"),
  "UserProfilePage"
);
const OnboardingPage = lazyNamed(
  () => import("@/pages/private/OnboardingPage/OnboardingPage"),
  "OnboardingPage"
);
const GloveboxPage = lazyNamed(
  () => import("@/pages/private/GloveboxPage/GloveboxPage"),
  "GloveboxPage"
);
const RevenuesPage = lazyNamed(
  () => import("@/pages/private/RevenuesPage/RevenuesPage"),
  "RevenuesPage"
);
const ExpenseSummaryReportPage = lazyNamed(
  () => import("@/pages/private/ExpenseSummaryReportPage/ExpenseSummaryReportPage"),
  "ExpenseSummaryReportPage"
);
const YearlyReportPage = lazyNamed(
  () => import("@/pages/private/YearlyReportPage/YearlyReportPage"),
  "YearlyReportPage"
);
const FuelEconomyReportPage = lazyNamed(
  () => import("@/pages/private/FuelEconomyReportPage/FuelEconomyReportPage"),
  "FuelEconomyReportPage"
);
const TravelReportPage = lazyNamed(
  () => import("@/pages/private/TravelReportPage/TravelReportPage"),
  "TravelReportPage"
);
const ProfitabilityReportPage = lazyNamed(
  () => import("@/pages/private/ProfitabilityReportPage/ProfitabilityReportPage"),
  "ProfitabilityReportPage"
);
const TcoReportPage = lazyNamed(
  () => import("@/pages/private/TcoReportPage/TcoReportPage"),
  "TcoReportPage"
);
const CarHandoverReportPage = lazyNamed(
  () => import("@/pages/private/CarHandoverReportPage/CarHandoverReportPage"),
  "CarHandoverReportPage"
);

const ParkingSessionsPage = lazyNamed(
  () => import("@/pages/private/ParkingSessionsPage/ParkingSessionsPage"),
  "ParkingSessionsPage"
);

const TravelsPage = lazyNamed(
  () => import("@/pages/private/TravelsPage/TravelsPage"),
  "TravelsPage"
);
const ExpenseSchedulesPage = lazyNamed(
  () => import("@/pages/private/ExpenseSchedulesPage/ExpenseSchedulesPage"),
  "ExpenseSchedulesPage"
);
const VehicleRecallsPage = lazyNamed(
  () => import("@/pages/private/VehicleRecallsPage/VehicleRecallsPage"),
  "VehicleRecallsPage"
);
const VehicleFinancingPage = lazyNamed(
  () => import("@/pages/private/VehicleFinancingPage/VehicleFinancingPage"),
  "VehicleFinancingPage"
);
const TireSetPage = lazyNamed(
  () => import("@/pages/private/TireSetPage/TireSetPage"),
  "TireSetPage"
);
const VehicleTasksPage = lazyNamed(
  () => import("@/pages/private/VehicleTasksPage/VehicleTasksPage"),
  "VehicleTasksPage"
);
const LogbookPage = lazyNamed(
  () => import("@/pages/private/LogbookPage/LogbookPage"),
  "LogbookPage"
);
const SavedPlacesPage = lazyNamed(
  () => import("@/pages/private/SavedPlacesPage/SavedPlacesPage"),
  "SavedPlacesPage"
);

export interface SiteMapItem {
  name: string;
  subtitle?: string;
  bottomNavBar?: number | null | undefined;
  url?: string;
  urlMask?: string;
  component?: React.ComponentType<any>;
  layout?: React.ComponentType<any>;
  visible?: boolean;
  roles?: string[];
  items?: SiteMapItem[];
  options?: {
    translationKey?: string;
  };
  icon?: string; // Icon name for navigation
  iconColor?: string; // Color for the icon (CSS color value)
}

export const componentsMap = {
  // Pages
  // DashboardPage,
  // AccountSettingsPage,
  // SubscriptionPage,
  // NotFoundPage,
  // ServerErrorPage,
  // ForgotPasswordPage,
  // ResetPasswordPage,
  // UserInvitationsPage,
  // AcceptInvitationPage,
  // UsersPage,
  // SigninPage,
  // SignupPage,
  // CarsPage,
  // RevenuesPage,
  // Layouts
  DashboardLayout,
};

const siteMap: SiteMapItem[] = [
  /* **********************************************************
   * Main Navigation - Daily Actions
   * ********************************************************** */
  {
    name: "Dashboard",
    subtitle: "Your control center",
    url: "/",
    bottomNavBar: 1,
    component: DashboardPage,
    visible: true,
    roles: ALL_ROLES,
    items: [],
    options: {
      translationKey: "dashboard",
    },
    icon: "Dashboard",
    iconColor: "#4C6EF5", // Blue - primary navigation hub
  },
  {
    name: "My Vehicles",
    subtitle: "Manage your cars and motorcycles",
    url: "/cars",
    component: CarsPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Vehicle",
        subtitle: "Add a new vehicle",
        url: "/cars/add",
        component: CarsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "carsAdd",
        },
        icon: "Car",
        iconColor: "#5C7CFA", // Indigo - vehicles
      },
      {
        name: "Edit Vehicle",
        subtitle: "Edit vehicle details",
        url: "/cars/:id/edit",
        component: CarsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "carsEdit",
        },
        icon: "Car",
        iconColor: "#5C7CFA", // Indigo - vehicles
      },
    ],
    options: {
      translationKey: "cars",
    },
    icon: "Car",
    iconColor: "#5C7CFA", // Indigo - vehicles
  },
  {
    name: "Glovebox",
    subtitle: "Store and manage vehicle documents and equipment",
    url: "/glovebox",
    bottomNavBar: 2,
    component: GloveboxPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Glovebox",
        subtitle: "Add a new item to the glovebox",
        url: "/glovebox/add",
        component: GloveboxPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "gloveboxAdd",
        },
        icon: "FileText",
        iconColor: "#0CA678", // Teal - documents
      },
      {
        name: "Glovebox",
        subtitle: "Edit glovebox item details",
        url: "/glovebox/:id/edit",
        component: GloveboxPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "gloveboxEdit",
        },
        icon: "FileText",
        iconColor: "#0CA678", // Teal - documents
      },
    ],
    options: {
      translationKey: "glovebox",
    },
    icon: "FileText",
    iconColor: "#0CA678", // Teal - documents
  },
  {
    name: "Logbook",
    subtitle: "Track refuels, expenses, and checkpoints",
    url: "/logbook",
    bottomNavBar: 3,
    component: LogbookPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Logbook",
        subtitle: "Track refuels, expenses, and checkpoints",
        url: "/logbook/add",
        component: LogbookPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "logbook",
        },
        icon: "Notebook",
        iconColor: "#2563EB", // Blue - logbook
      },
      {
        name: "Logbook",
        subtitle: "Track refuels, expenses, and checkpoints",
        url: "/logbook/:id/edit",
        component: LogbookPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "logbook",
        },
        icon: "Notebook",
        iconColor: "#2563EB", // Blue - logbook
      },
    ],
    options: {
      translationKey: "logbook",
    },
    icon: "Notebook",
    iconColor: "#2563EB", // Blue - logbook
  },
  {
    name: "Parking Sessions",
    subtitle: "Track and review parking history",
    url: "/parking-sessions",
    component: ParkingSessionsPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Pakring Session",
        subtitle: "Create a record of past parking session",
        url: "/parking-sessions/add",
        component: ParkingSessionsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "parkingSessions",
        },
        icon: "Parking",
        iconColor: "#1C7ED6",
      },
      {
        name: "Edit Parking Session",
        subtitle: "Edit a record of past parking session",
        url: "/parking-sessions/:id/edit",
        component: ParkingSessionsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "parkingSessions",
        },
        icon: "Parking",
        iconColor: "#1C7ED6",
      },
    ],
    options: {
      translationKey: "parkingSessions",
    },
    icon: "Parking",
    iconColor: "#1C7ED6", // Blue - parking
  },
  {
    name: "Travels",
    subtitle: "Track your trips and mileage",
    url: "/travels",
    bottomNavBar: 4,
    component: TravelsPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Travel",
        subtitle: "Log a past trip",
        url: "/travels/add",
        component: TravelsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "travels",
        },
        icon: "Route",
        iconColor: "#2F9E44", // Green - trips/roads
      },
      {
        name: "Edit Travel",
        subtitle: "Edit trip details",
        url: "/travels/:id/edit",
        component: TravelsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "travels",
        },
        icon: "Route",
        iconColor: "#2F9E44", // Green - trips/roads
      },
      {
        name: "Travel Waypoints",
        subtitle: "View trip waypoints",
        url: "/travels/:id/waypoints",
        component: TravelsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "travelsWaypoints",
        },
        icon: "Route",
        iconColor: "#2F9E44", // Green - trips/roads
      },
    ],
    options: {
      translationKey: "travels",
    },
    icon: "Route",
    iconColor: "#2F9E44", // Green - trips/roads
  },
  {
    name: "Revenues",
    subtitle: "Track vehicle-related income",
    url: "/revenues",
    component: RevenuesPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Revenue",
        subtitle: "Add a new revenue record",
        url: "/revenues/add",
        component: RevenuesPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "revenues",
        },
        icon: "Coins",
        iconColor: "#2B8A3E", // Emerald green - income/money in
      },
      {
        name: "Edit Revenue",
        subtitle: "Edit revenue details",
        url: "/revenues/:id/edit",
        component: RevenuesPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "revenues",
        },
        icon: "Coins",
        iconColor: "#2B8A3E", // Emerald green - income/money in
      },
    ],
    options: {
      translationKey: "revenues",
    },
    icon: "Coins",
    iconColor: "#2B8A3E", // Emerald green - income/money in
  },
  {
    name: "Tire Sets",
    subtitle: "Track seasonal and replacement tires",
    url: "/tire-sets",
    component: TireSetPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Tire Set",
        subtitle: "Create a new tire set record",
        url: "/tire-sets/add",
        component: TireSetPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "tireSets",
        },
        icon: "Wheel",
        iconColor: "#495057", // Dark gray - rubber/tires
      },
      {
        name: "Edit Tire Set",
        subtitle: "Edit tire set details",
        url: "/tire-sets/:id/edit",
        component: TireSetPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "tireSets",
        },
        icon: "Wheel",
        iconColor: "#495057", // Dark gray - rubber/tires
      },
    ],
    options: {
      translationKey: "tireSets",
    },
    icon: "Wheel",
    iconColor: "#495057", // Dark gray - rubber/tires
  },
  {
    name: "Vehicle Financing",
    subtitle: "Track loans and leases for your vehicles",
    url: "/vehicle-financing",
    component: VehicleFinancingPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Vehicle Financing",
        subtitle: "Track loans and leases for your vehicles",
        url: "/vehicle-financing/add",
        component: VehicleFinancingPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "vehicleFinancing",
        },
        icon: "CreditCard",
        iconColor: "#7950F2", // Purple - loans/finance
      },
      {
        name: "Vehicle Financing",
        subtitle: "Track loans and leases for your vehicles",
        url: "/vehicle-financing/:id/edit",
        component: VehicleFinancingPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "vehicleFinancing",
        },
        icon: "CreditCard",
        iconColor: "#7950F2", // Purple - loans/finance
      },
    ],
    options: {
      translationKey: "vehicleFinancing",
    },
    icon: "CreditCard",
    iconColor: "#7950F2", // Purple - loans/finance
  },
  {
    name: "Vehicle Tasks",
    subtitle: "Manage vehicle to-do items",
    url: "/vehicle-tasks",
    component: VehicleTasksPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Task",
        subtitle: "Create a new vehicle task",
        url: "/vehicle-tasks/add",
        component: VehicleTasksPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "vehicleTasks",
        },
        icon: "Checklist",
        iconColor: "#F59F00", // Amber - to-do/action items
      },
      {
        name: "Edit Task",
        subtitle: "Edit vehicle task details",
        url: "/vehicle-tasks/:id/edit",
        component: VehicleTasksPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "vehicleTasks",
        },
        icon: "Checklist",
        iconColor: "#F59F00", // Amber - to-do/action items
      },
    ],
    options: {
      translationKey: "vehicleTasks",
    },
    icon: "Checklist",
    iconColor: "#F59F00", // Amber - to-do/action items
  },
  {
    name: "Saved Places",
    subtitle: "Manage your frequently visited locations",
    url: "/saved-places",
    component: SavedPlacesPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Saved Places",
        subtitle: "Manage your frequently visited locations",
        url: "/saved-places/add",
        component: SavedPlacesPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
        items: [],
        options: {
          translationKey: "savedPlaces",
        },
        icon: "MapPin",
        iconColor: "#0891B2", // Cyan - saved places
      },
      {
        name: "Saved Places",
        subtitle: "Manage your frequently visited locations",
        url: "/saved-places/:id/edit",
        component: SavedPlacesPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "savedPlaces",
        },
        icon: "MapPin",
        iconColor: "#0891B2", // Cyan - saved places
      },
    ],
    options: {
      translationKey: "savedPlaces",
    },
    icon: "MapPin",
    iconColor: "#0891B2", // Cyan - saved places
  },


  /* **********************************************************
   * Reports Section
   * ********************************************************** */
  {
    name: "Reports",
    subtitle: "View expense reports and analytics",
    url: "/reports",
    component: ExpenseSummaryReportPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Upcoming Services",
        subtitle: "Track and manage scheduled vehicle maintenance",
        url: "/reports/upcoming-services",
        component: UpcomingServicesPage,
        visible: true,
        roles: ALL_ROLES,
        items: [
          {
            name: "Edit Service Schedule",
            subtitle: "Edit service schedule",
            url: "/reports/upcoming-services/:id/edit",
            component: UpcomingServicesPage,
            visible: false,
            roles: ALL_ROLES,
            items: [],
            options: {
              translationKey: "upcomingServices",
            },
            icon: "CalendarClock",
            iconColor: "#D6336C", // Pink - maintenance urgency
          },
        ],
        options: {
          translationKey: "upcomingServices",
        },
        icon: "CalendarClock",
        iconColor: "#D6336C", // Pink - maintenance urgency
      },
      {
        name: "TCO Report",
        subtitle: "True cost of ownership per vehicle",
        url: "/reports/tco",
        component: TcoReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "tcoReport",
        },
        icon: "ReportMoney",
        iconColor: "#1971C2", // Blue - lifetime cost tracking
      },
      {
        name: "Fuel Economy",
        subtitle: "Consumption analysis and trends",
        url: "/reports/fuel-economy",
        component: FuelEconomyReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "fuelEconomyReport",
        },
        icon: "GasStation",
        iconColor: "#E8590C", // Orange - fuel (matches refuels)
      },
      {
        name: "Expense Summary",
        subtitle: "Overview of all vehicle expenses",
        url: "/reports/expense-summary",
        component: ExpenseSummaryReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "expenseSummary",
        },
        icon: "ChartPie",
        iconColor: "#E03131", // Red - expense analysis (matches expenses)
      },
      {
        name: "Yearly Report",
        subtitle: "Month-by-month expense breakdown",
        url: "/reports/yearly",
        component: YearlyReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "yearlyReport",
        },
        icon: "ChartBar",
        iconColor: "#3B5BDB", // Deep blue - yearly overview
      },
      {
        name: "Travel Report",
        subtitle: "Track trips and calculate tax deductions",
        url: "/reports/travel",
        component: TravelReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "travelReport",
        },
        icon: "Route",
        iconColor: "#2F9E44", // Green - travel (matches travels)
      },
      {
        name: "Profitability Report",
        subtitle: "Analyze vehicle revenue vs expenses",
        url: "/reports/profitability",
        component: ProfitabilityReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "profitabilityReport",
        },
        icon: "ChartLine",
        iconColor: "#2B8A3E", // Emerald - profitability (matches revenues)
      },
      {
        name: "Car Handover Report",
        subtitle: "Service history report for vehicle sale",
        url: "/reports/handover",
        component: CarHandoverReportPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "carHandoverReport",
        },
        icon: "TransferOut",
        iconColor: "#2F9E44", // Green - handover/transfer
      },
      {
        name: "Vehicle Recalls",
        subtitle: "Safety recalls for your vehicles (the US and Canada only)",
        url: "/reports/vehicle-recalls",
        component: VehicleRecallsPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "vehicleRecalls",
        },
        icon: "ShieldExclamation",
        iconColor: "#C92A2A", // Deep red - safety warnings
      },
    ],
    options: {
      translationKey: "reports",
    },
    icon: "ChartBar",
    iconColor: "#3B5BDB", // Deep blue - analytics/reports
  },

  /* **********************************************************
   * Settings Section - Configuration & Preferences
   * ********************************************************** */
  {
    name: "Settings",
    subtitle: "Configure your account",
    url: "/settings",
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Preferences",
        subtitle: "Customize units, currency, and notifications",
        url: "/settings/preferences",
        component: UserPreferencesPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "preferences",
        },
        icon: "Adjustments",
        iconColor: "#868E96", // Gray - configuration
      },
      {
        name: "Tags",
        subtitle: "Manage expense tags",
        url: "/settings/tags",
        component: TagsPage,
        visible: true,
        roles: ALL_ROLES,
        items: [
          {
            name: "Add Tag",
            subtitle: "Create a new tag",
            url: "/settings/tags/add",
            component: TagsPage,
            visible: false,
            roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
            items: [],
            options: {
              translationKey: "tags",
            },
            icon: "Tag",
            iconColor: "#AE3EC9", // Grape - labels/tags
          },
          {
            name: "Edit Tag",
            subtitle: "Edit tag details",
            url: "/settings/tags/:id/edit",
            component: TagsPage,
            visible: false,
            roles: ALL_ROLES,
            items: [],
            options: {
              translationKey: "tags",
            },
            icon: "Tag",
            iconColor: "#AE3EC9", // Grape - labels/tags
          },
        ],
        options: {
          translationKey: "tags",
        },
        icon: "Tag",
        iconColor: "#AE3EC9", // Grape - labels/tags
      },
      {
        name: "Service Intervals",
        subtitle: "Configure maintenance reminders",
        url: "/settings/service-intervals",
        component: ServiceIntervalsPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {
          translationKey: "serviceIntervals",
        },
        icon: "Clock",
        iconColor: "#D6336C", // Pink - maintenance (matches upcoming services)
      },
      {
        name: "Scheduled Expenses",
        subtitle: "Automate recurring vehicle expenses",
        url: "/settings/expense-schedules",
        component: ExpenseSchedulesPage,
        visible: true,
        roles: ALL_ROLES,
        items: [
          {
            name: "Add Scheduled Expense",
            subtitle: "Create a new expense schedule",
            url: "/settings/expense-schedules/add",
            component: ExpenseSchedulesPage,
            visible: false,
            roles: [...MANAGER_ROLES, USER_ROLES.DRIVER],
            items: [],
            options: {
              translationKey: "expenseSchedules",
            },
            icon: "CalendarClock",
            iconColor: "#E03131", // Red - scheduled expenses (matches expenses)
          },
          {
            name: "Edit Scheduled Expense",
            subtitle: "Edit expense schedule details",
            url: "/settings/expense-schedules/:id/edit",
            component: ExpenseSchedulesPage,
            visible: false,
            roles: ALL_ROLES,
            items: [],
            options: {
              translationKey: "expenseSchedules",
            },
            icon: "CalendarClock",
            iconColor: "#E03131", // Red - scheduled expenses (matches expenses)
          },
        ],
        options: {
          translationKey: "expenseSchedules",
        },
        icon: "CalendarClock",
        iconColor: "#E03131", // Red - scheduled expenses (matches expenses)
      },
    ],
    options: {
      translationKey: "settings",
    },
    icon: "Settings",
    iconColor: "#868E96", // Gray - settings/configuration
  },

  /* **********************************************************
   * Team Section - User Management
   * ********************************************************** */
  {
    name: "Team",
    subtitle: "Manage users and access",
    url: "/team",
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Users",
        subtitle: "Manage users of your account",
        url: "/team/users",
        component: UsersPage,
        visible: true,
        roles: ALL_ROLES,
        items: [
          {
            name: "Edit User",
            subtitle: "Edit user details",
            url: "/team/users/:id/edit",
            component: UsersPage,
            visible: false,
            roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
            items: [],
            options: {
              translationKey: "users",
            },
            icon: "User",
            iconColor: "#7048E8", // Violet - people/team
          },
        ],
        options: {
          translationKey: "users",
        },
        icon: "Users",
        iconColor: "#7048E8", // Violet - people/team
      },
      {
        name: "Invitations",
        subtitle: "Manage invitations to your account",
        url: "/team/invitations",
        component: UserInvitationsPage,
        visible: true,
        roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
        items: [
          {
            name: "Invite User",
            subtitle: "Invite new user to be part of your team",
            url: "/team/invitations/add",
            component: UserInvitationsPage,
            visible: false,
            roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
            items: [],
            options: {
              translationKey: "userInvitations",
            },
            icon: "UserPlus",
            iconColor: "#7048E8", // Violet - people/team
          },
          {
            name: "Edit Invitation",
            subtitle: "Edit invitation details",
            url: "/team/invitations/:id/edit",
            component: UserInvitationsPage,
            visible: false,
            roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
            items: [],
            options: {
              translationKey: "userInvitations",
            },
            icon: "User",
            iconColor: "#7048E8", // Violet - people/team
          },
        ],
        options: {
          translationKey: "userInvitations",
        },
        icon: "UserPlus",
        iconColor: "#7048E8", // Violet - people/team
      },
    ],
    options: {
      translationKey: "team",
    },
    icon: "Users",
    iconColor: "#7048E8", // Violet - people/collaboration
  },

  /* **********************************************************
   * User Menu Items (typically shown in avatar dropdown)
   * ********************************************************** */
  {
    name: "User Profile",
    subtitle: "Manage your profile",
    url: "/account/profile",
    component: UserProfilePage,
    visible: false, // Hidden from main nav, shown in user dropdown
    roles: ALL_ROLES,
    items: [],
    options: {
      translationKey: "userProfilePage",
    },
    icon: "User",
    iconColor: "#495057", // Dark gray - personal/account
  },
  {
    name: "Account Settings",
    subtitle: "Manage your account settings",
    url: "/account/settings",
    component: AccountSettingsPage,
    visible: false, // Hidden from main nav, shown in user dropdown for owner/admin
    roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
    items: [],
    options: {
      translationKey: "accountSettings",
    },
    icon: "Settings",
    iconColor: "#868E96", // Gray - settings
  },
  {
    name: "Subscription",
    subtitle: "Manage your subscription",
    url: "/account/subscription",
    component: SubscriptionPage,
    visible: false, // Hidden from main nav, shown in user dropdown for owner
    roles: [USER_ROLES.OWNER],
    items: [],
    options: {
      translationKey: "subscription",
    },
    icon: "CreditCard",
    iconColor: "#7950F2", // Purple - billing/finance
  },
  {
    name: "Onboarding",
    subtitle: "Set up your account",
    url: "/onboarding",
    component: OnboardingPage,
    visible: false,
    roles: ALL_ROLES,
    items: [],
    options: {
      translationKey: "onboarding",
    },
    icon: "Rocket",
    iconColor: "#4C6EF5", // Blue - getting started
  },

  /* **********************************************************
   * Public Pages
   * ********************************************************** */
  {
    name: "Sign Up",
    subtitle: "Create your Car Expenses account",
    url: "/account/signup",
    component: SignupPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "signUp",
    },
  },
  {
    name: "Sign In",
    subtitle: "Sign in to your Car Expenses account",
    url: "/account/signin",
    component: SigninPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "signIn",
    },
  },
  {
    name: "Forgot Password",
    subtitle: "Request password reset link",
    url: "/account/forgot-password",
    component: ForgotPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "forgotPassword",
    },
  },
  {
    name: "Reset Password",
    subtitle: "Reset password",
    url: "/account/reset-password",
    component: ResetPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "resetPassword",
    },
  },
  {
    name: "Accept Invitation",
    subtitle: "Accept invitation to join company at Car Expenses",
    url: "/account/accept-invitation",
    component: AcceptInvitationPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "acceptInvitation",
    },
  },
  {
    name: "Server Error Page",
    subtitle: "",
    url: "/server-error",
    component: ServerErrorPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "serverError",
    },
  },
  // Not Found
  {
    name: "Page Not Found",
    subtitle: "",
    url: "*",
    component: NotFoundPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {
      translationKey: "notFound",
    },
  },
];

export default siteMap;